import styled, { css } from 'styled-components'
import {
  StyledInputProps,
  InputWrapperProps,
  IconWrapperProps,
} from './Input.types'

const sizeStyles = {
  small: css`
    height: 32px;
    padding: 0 ${({ theme }) => theme.spacing.small};
    font-size: ${({ theme }) => theme.typography.fontSize.small};
  `,
  medium: css`
    height: 40px;
    padding: 0 ${({ theme }) => theme.spacing.medium};
    font-size: ${({ theme }) => theme.typography.fontSize.body};
  `,
  large: css`
    height: 48px;
    padding: 0 ${({ theme }) => theme.spacing.large};
    font-size: ${({ theme }) => theme.typography.fontSize.section};
  `,
}

const variantStyles = {
  default: css<{ $error?: boolean }>`
    background-color: ${({ theme }) => theme.colors.background.primary};
    border: 1px solid
      ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      box-shadow: ${(props) =>
        props.$error ? props.theme.shadows.error : props.theme.shadows.focus};
    }
  `,
  filled: css<{ $error?: boolean }>`
    background-color: ${({ theme }) => theme.colors.background.secondary};
    border: 1px solid transparent;
    border-bottom-color: ${(props) =>
      props.$error
        ? props.theme.colors.border.error
        : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-bottom-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-bottom-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      border-bottom-width: 2px;
      padding-bottom: calc(0px - 1px);
      box-shadow: none;
    }
  `,
  outlined: css<{ $error?: boolean }>`
    background-color: transparent;
    border: 2px solid
      ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.default};

    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.light};
    }

    &:focus {
      border-color: ${(props) =>
        props.$error
          ? props.theme.colors.border.error
          : props.theme.colors.border.focus};
      box-shadow: none;
    }
  `,
}

export const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  display: ${(props) => (props.$fullWidth ? 'block' : 'inline-block')};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
`

export const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  outline: none;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${(props) => sizeStyles[props.$size]}
  ${(props) => variantStyles[props.$variant]}
  
  ${(props) =>
    props.$hasLeftIcon &&
    css`
      padding-left: ${props.$size === 'small'
        ? '36px'
        : props.$size === 'large'
          ? '52px'
          : '44px'};
    `}
  
  ${(props) =>
    props.$hasRightIcon &&
    css`
      padding-right: ${props.$size === 'small'
        ? '36px'
        : props.$size === 'large'
          ? '52px'
          : '44px'};
    `}
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const IconWrapper = styled.div<IconWrapperProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${(props) => (props.$isClickable ? 'auto' : 'none')};
  color: ${({ theme }) => theme.colors.text.secondary};

  ${(props) =>
    props.$position === 'left'
      ? css`
          left: ${props.$size === 'small'
            ? props.theme.spacing.small
            : props.$size === 'large'
              ? props.theme.spacing.medium
              : '10px'};
        `
      : css`
          right: ${props.$size === 'small'
            ? props.theme.spacing.small
            : props.$size === 'large'
              ? props.theme.spacing.medium
              : '10px'};
        `}
`
